import type { FC } from "react";
import { useState } from "react";
import { publishManager } from "./publish-manager";
import { useSnapshot } from "valtio";
import { PublishableTypes } from "@renewmap/model";
import { Button } from "@renewmap/ui/button";
import { AlertTriangle, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@renewmap/ui/dialog";

export const InvalidEntityRecordsCard: FC = () => {
  const { invalidEntityRecordIds, deletingInvalidEntityRecords } = useSnapshot(
    publishManager.publishState,
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);

  // Calculate counts from invalidEntityRecordIds
  const invalidEntityRecordCounts: Partial<Record<PublishableTypes, number>> =
    {};
  for (const tableName in invalidEntityRecordIds) {
    invalidEntityRecordCounts[tableName as PublishableTypes] =
      invalidEntityRecordIds[tableName as PublishableTypes]?.length || 0;
  }

  // Get all tables that have invalid entity records
  const tablesWithInvalidRecords = Object.keys(
    invalidEntityRecordCounts,
  ).filter(
    (tableName) =>
      (invalidEntityRecordCounts[tableName as PublishableTypes] || 0) > 0,
  );

  // Calculate total invalid entity record count
  const totalInvalidRecords = tablesWithInvalidRecords.reduce(
    (sum, tableName) =>
      sum + (invalidEntityRecordCounts[tableName as PublishableTypes] || 0),
    0,
  );

  // Check if any table is deleting
  const isAnyDeleting = tablesWithInvalidRecords.some(
    (tableName) => deletingInvalidEntityRecords[tableName as PublishableTypes],
  );

  // If no invalid entity records, don't render anything
  if (tablesWithInvalidRecords.length === 0) {
    return null;
  }

  const handleDeleteAll = async () => {
    setShowDeleteAllDialog(false);
    await publishManager.deleteAllInvalidEntityRecords();
  };

  return (
    <div className="rounded-lg border border-orange-300 bg-orange-50 p-6">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex-1">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-orange-800">
            <AlertTriangle className="h-5 w-5" />
            Invalid Entity Records in Draft
          </h2>
          <p className="mt-1 text-sm text-orange-700">
            These rows reference deleted entities and should be removed from
            draft
          </p>
        </div>
        <div className="ml-4 flex gap-2">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowDeleteAllDialog(true)}
            disabled={isAnyDeleting}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {isAnyDeleting
              ? "Deleting..."
              : `Delete All (${totalInvalidRecords})`}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="mr-2 h-4 w-4" />
                Hide
              </>
            ) : (
              <>
                <ChevronDown className="mr-2 h-4 w-4" />
                Show
              </>
            )}
          </Button>
        </div>
      </div>
      {isExpanded && (
        <div className="space-y-4">
          {tablesWithInvalidRecords.map((tableName) => (
            <InvalidEntityRecordTableCard
              key={tableName}
              tableName={tableName as PublishableTypes}
              count={
                invalidEntityRecordCounts[tableName as PublishableTypes] || 0
              }
              isDeleting={
                deletingInvalidEntityRecords[tableName as PublishableTypes] ||
                false
              }
            />
          ))}
        </div>
      )}

      <Dialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete All Invalid Entity Records</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete all {totalInvalidRecords} invalid
              entity {totalInvalidRecords === 1 ? "record" : "records"} across{" "}
              {tablesWithInvalidRecords.length}{" "}
              {tablesWithInvalidRecords.length === 1 ? "table" : "tables"}?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              This action cannot be undone. These rows reference deleted
              entities and will be permanently removed from the draft schema.
            </p>
            <div>
              <p className="mb-2 text-sm font-medium">Tables affected:</p>
              <ul className="text-muted-foreground list-inside list-disc text-sm">
                {tablesWithInvalidRecords.map((tableName) => (
                  <li key={tableName}>
                    {tableName}:{" "}
                    {invalidEntityRecordCounts[tableName as PublishableTypes] ||
                      0}{" "}
                    rows
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteAllDialog(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteAll}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete All {totalInvalidRecords}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface InvalidEntityRecordTableCardProps {
  tableName: PublishableTypes;
  count: number;
  isDeleting: boolean;
}

const InvalidEntityRecordTableCard: FC<InvalidEntityRecordTableCardProps> = ({
  tableName,
  count,
  isDeleting,
}) => {
  return (
    <div className="rounded-lg border border-orange-200 bg-white">
      <div className="flex items-center justify-between border-b border-orange-200 bg-orange-50 p-4">
        <div className="flex-1">
          <div className="font-semibold text-orange-900">{tableName}</div>
          <div className="text-sm text-orange-700">
            {count} invalid entity {count === 1 ? "record" : "records"}
            {isDeleting && " (deleting...)"}
          </div>
        </div>
      </div>
    </div>
  );
};
